import { Routes } from '@angular/router';
import { guestGuard } from './core/guards/guest.guard';

export const routes: Routes = [
  // Auth — guest only, separate layout
  {
    path: 'auth',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./layouts/auth-layout/auth-layout.component').then(
        (m) => m.AuthLayoutComponent,
      ),
    loadChildren: () =>
      import('./features/auth/auth.routes').then((m) => m.authRoutes),
  },

  // Logout — clears auth and redirects to /auth
  {
    path: 'logout',
    loadComponent: () =>
      import('./features/auth/logout/logout.component').then((m) => m.LogoutComponent),
  },

  // Main app — layout wraps all feature routes
  {
    path: '',
    loadComponent: () =>
      import('./layouts/main-layout/main-layout.component').then(
        (m) => m.MainLayoutComponent,
      ),
    children: [
      { path: '', redirectTo: 'feed', pathMatch: 'full' },
      {
        path: 'privacy-policy',
        loadComponent: () =>
          import('./features/home/static/privacy-policy/privacy-policy.component').then(
            (m) => m.PrivacyPolicyComponent,
          ),
      },
      {
        path: 'terms-condition',
        loadComponent: () =>
          import('./features/home/static/terms-condition/terms-condition.component').then(
            (m) => m.TermsConditionComponent,
          ),
      },
      {
        path: 'feed',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/home/feed/feed.component').then((m) => m.FeedComponent),
          },
          {
            path: 'authors',
            loadComponent: () =>
              import('./features/home/feed/authors/authors-list.component').then(
                (m) => m.AuthorsListComponent,
              ),
          },
          {
            path: 'authors/:id',
            loadComponent: () =>
              import('./features/home/feed/author-detail/author-detail.component').then(
                (m) => m.AuthorDetailComponent,
              ),
          },
          {
            path: 'post/:type/:id',
            loadComponent: () =>
              import('./features/home/feed/post-detail/post-detail.component').then(
                (m) => m.PostDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'preorder',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/user/preorder/preorder.component').then((m) => m.PreorderComponent),
          },
          // Phone flow: quantity picker and basket are full pages (desktop/tablet use dialogs)
          {
            path: 'basket',
            loadComponent: () =>
              import('./features/user/preorder/pages/preorder-basket-page.component').then(
                (m) => m.PreorderBasketPageComponent,
              ),
          },
          {
            path: 'product/:id',
            loadComponent: () =>
              import('./features/user/preorder/pages/preorder-product-page.component').then(
                (m) => m.PreorderProductPageComponent,
              ),
          },
        ],
      },
      {
        path: 'click',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/user/click/click.component').then((m) => m.ClickComponent),
          },
          // Phone flow: the choice of the price option and the basket are full pages (desktop/tablet use dialogs)
          {
            path: 'basket',
            loadComponent: () =>
              import('./features/user/click/pages/lot-basket-page.component').then(
                (m) => m.LotBasketPageComponent,
              ),
          },
          {
            path: 'product/:id',
            loadComponent: () =>
              import('./features/user/click/pages/lot-buy-page.component').then(
                (m) => m.LotBuyPageComponent,
              ),
          },
        ],
      },
      {
        path: 'orders',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/user/orders/orders.component').then((m) => m.OrdersComponent),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/user/orders/order-detail/order-detail.component').then(
                (m) => m.OrderDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'warehouses',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/user/warehouses/warehouses.component').then((m) => m.WarehousesComponent),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/user/warehouses/warehouses-detail/warehouses-detail.component').then(
                (m) => m.WarehousesDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'chats',
        loadComponent: () =>
          import('./features/user/chats/chats.component').then((m) => m.ChatsComponent),
      },
      {
        path: 'my-shops',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/user/my-shops/my-shops.component').then((m) => m.MyShopsComponent),
          },
          {
            path: 'create',
            loadComponent: () =>
              import('./features/user/my-shops/shop-form/shop-form.component').then((m) => m.ShopFormComponent),
          },
          {
            path: 'create/stores',
            loadComponent: () =>
              import('./features/user/my-shops/shop-stores/shop-stores.component').then((m) => m.ShopStoresComponent),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/user/my-shops/shop-detail/shop-detail.component').then(
                (m) => m.ShopDetailComponent,
              ),
          },
          {
            path: ':id/edit',
            loadComponent: () =>
              import('./features/user/my-shops/shop-form/shop-form.component').then((m) => m.ShopFormComponent),
          },
          {
            path: ':id/edit/stores',
            loadComponent: () =>
              import('./features/user/my-shops/shop-stores/shop-stores.component').then((m) => m.ShopStoresComponent),
          },
          {
            path: ':id/stores',
            loadComponent: () =>
              import('./features/user/my-shops/shop-stores/shop-stores.component').then((m) => m.ShopStoresComponent),
          },
        ],
      },
      {
        path: 'acts',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/user/acts/acts-list/acts.component').then((m) => m.ActsComponent),
          },
          {
            path: 'create',
            loadComponent: () =>
              import('./features/user/acts/act-create-page/act-create-page.component').then(
                (m) => m.ActCreatePageComponent,
              ),
          },
        ],
      },
      {
        path: 'claims',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/user/claims/claims-list/claims.component').then((m) => m.ClaimsComponent),
          },
          {
            path: 'create',
            loadComponent: () =>
              import('./features/user/claims/claim-create-page/claim-create-page.component').then(
                (m) => m.ClaimCreatePageComponent,
              ),
          },
          {
            path: 'rules',
            loadComponent: () =>
              import('./features/user/claims/claim-rules-page/claim-rules-page.component').then(
                (m) => m.ClaimRulesPageComponent,
              ),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/user/claims/claim-detail/claim-detail.component').then(
                (m) => m.ClaimDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/user/profile/profile-page/profile.component').then((m) => m.ProfileComponent),
          },
          {
            path: 'delete-account',
            loadComponent: () =>
              import('./features/user/profile/delete-account-page/delete-account-page.component').then(
                (m) => m.DeleteAccountPageComponent,
              ),
          },
        ],
      },
      {
        path: 'documents',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/user/documents/documents.component').then((m) => m.DocumentsComponent),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/user/documents/document-detail/document-detail.component').then(
                (m) => m.DocumentDetailComponent,
              ),
          },
        ],
      },
      { path: '**', redirectTo: 'feed' },
    ],
  },
];
